import { LocationModel } from './location.class';
import type { LocationType, MappingLocationsResponse } from '@/entities/dashboard/mapping';
import type { LocationStatistics } from './types';

export class LocationService {
  private locations: Record<string, LocationModel> = {};
  private sites: Record<string, string> = {};
  private regions: Record<string, string> = {};
  private subregions: Record<string, string> = {};
  private countries: Record<string, string> = {};

  loadFromResponse(response: MappingLocationsResponse): void {
    this.locations = {};
    this.sites = {};
    this.regions = {};
    this.subregions = {};
    this.countries = {};

    response.locations.forEach((loc) => {
      this.locations[loc.id] = LocationModel.fromResponse(loc);
    });

    response.sites.forEach((site) => {
      this.sites[String(site.id)] = site.name;
    });

    response.regions.forEach((region) => {
      this.regions[String(region.id)] = region.name;
    });

    response.subregions.forEach((subregion) => {
      this.subregions[String(subregion.id)] = subregion.name;
    });

    response.countries.forEach((country) => {
      this.countries[String(country.id)] = country.name;
    });
  }

  getAll(): LocationModel[] {
    return Object.values(this.locations);
  }

  getById(id: string): LocationModel | undefined {
    return this.locations[id];
  }

  getByType(type: LocationType): LocationModel[] {
    return this.getAll().filter((loc) => loc.type === type);
  }

  getBySite(siteId: string): LocationModel[] {
    return this.getAll().filter((loc) => loc.siteId === siteId);
  }

  getByRegion(regionId: string): LocationModel[] {
    return this.getAll().filter((loc) => loc.regionId === regionId);
  }

  getByCountry(countryId: string): LocationModel[] {
    return this.getAll().filter((loc) => loc.countryId === countryId);
  }

  getSiteName(siteId: string): string {
    return this.sites[siteId] || 'Unknown';
  }

  getRegionName(regionId: string): string {
    return this.regions[regionId] || 'Unknown';
  }

  getSubregionName(subregionId: string): string {
    return this.subregions[subregionId] || 'Unknown';
  }

  getCountryName(countryId: string): string {
    return this.countries[countryId] || 'Unknown';
  }

  getStatistics(): LocationStatistics {
    const all = this.getAll();
    const byRegion: Record<string, number> = {};
    const byCountry: Record<string, number> = {};

    all.forEach((loc) => {
      byRegion[loc.regionId] = (byRegion[loc.regionId] || 0) + 1;
      byCountry[loc.countryId] = (byCountry[loc.countryId] || 0) + 1;
    });

    return {
      totalLocations: all.length,
      warehouseCount: this.getByType('W').length,
      portCount: this.getByType('P').length,
      customerCount: this.getByType('C').length,
      siteCount: this.getByType('S').length,
      byRegion,
      byCountry
    };
  }

  findNearestLocation(lat: number, lng: number, type?: LocationType): LocationModel | undefined {
    const tempLocation = new LocationModel('temp', 'temp', 'S', lat, lng, '0', '0', '0', '0');
    const locations = type ? this.getByType(type) : this.getAll();

    if (locations.length === 0) return undefined;

    let nearest = locations[0];
    let minDistance = tempLocation.distanceTo(nearest);

    locations.slice(1).forEach((loc) => {
      const distance = tempLocation.distanceTo(loc);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = loc;
      }
    });

    return nearest;
  }

  getLocationsByDistance(
    fromLocation: LocationModel,
    maxDistance: number,
    type?: LocationType
  ): Array<{ location: LocationModel; distance: number }> {
    const locations = type ? this.getByType(type) : this.getAll();

    return locations
      .filter((loc) => loc.id !== fromLocation.id)
      .map((loc) => ({
        location: loc,
        distance: fromLocation.distanceTo(loc)
      }))
      .filter(({ distance }) => distance <= maxDistance)
      .sort((a, b) => a.distance - b.distance);
  }

  groupByType(): Record<LocationType, LocationModel[]> {
    const groups: Record<LocationType, LocationModel[]> = {
      W: [],
      P: [],
      C: [],
      S: []
    };

    this.getAll().forEach((loc) => {
      if (groups[loc.type]) {
        groups[loc.type].push(loc);
      }
    });

    return groups;
  }

  searchLocations(query: string): LocationModel[] {
    const lowerQuery = query.toLowerCase();
    return this.getAll().filter(
      (loc) =>
        loc.name.toLowerCase().includes(lowerQuery) ||
        loc.displayName.toLowerCase().includes(lowerQuery) ||
        this.getSiteName(loc.siteId).toLowerCase().includes(lowerQuery) ||
        this.getRegionName(loc.regionId).toLowerCase().includes(lowerQuery)
    );
  }
}
